import React, { JSX, SetStateAction, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'
import { Location } from '@leid/api-locations'
import { GetPriceResponse } from '@leid/api-orders'
import { FormikErrors, FormikTouched } from 'formik'
import { usePathname, useRouter } from 'next/navigation'

import { CustomerInfo } from 'app/utils/authentication'
import { buildDate } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { CartFormValues, OrderItemInfo } from 'appTypes/orders'
import { Button, ButtonSecondary, ButtonWhite } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'

import { SubmitStatus } from '../../interfaces'
import OrderPreSummaryBox from '../OrderPreSummaryBox'
import QuotationRequestForm from '../QuotationRequestForm'
import SendServiceRequestForm from '../SendServiceRequestForm'

import { RentalSectionFooterWrapper } from './styles'

type Props = {
  isProlonging: boolean
  isEditMode: boolean
  isSubmitting: boolean
  submitStatus: SubmitStatus
  isRentalBeyondMaxTime: boolean
  handleSubmit: VoidFunction
  values: CartFormValues
  step: number
  goToNextStep: VoidFunction
  goToPrevStep: VoidFunction
  touched: FormikTouched<CartFormValues>
  setTouched: (
    touched: FormikTouched<CartFormValues>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<CartFormValues>>
  setValues: (
    values: SetStateAction<CartFormValues>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<CartFormValues>>
  isRerent: boolean
  product: ItemApi
  productPrice: GetPriceResponse | null
  nextButtonDisabled: boolean
  regionHasAvailableLocations: boolean | undefined
  customerInfo: CustomerInfo
  orderItemtInfo: OrderItemInfo | undefined
  productLocations: Location[]
  productPriceLoading: boolean
}

const RentalSectionFooter: React.FC<Props> = ({
  isProlonging,
  isEditMode,
  isSubmitting,
  submitStatus,
  isRentalBeyondMaxTime,
  handleSubmit,
  values,
  step,
  goToNextStep,
  goToPrevStep,
  touched,
  setTouched,
  setValues,
  isRerent,
  product,
  productPrice,
  nextButtonDisabled,
  regionHasAvailableLocations,
  customerInfo,
  orderItemtInfo,
  productLocations,
  productPriceLoading,
}) => {
  const { t } = useTranslation(['common'])

  const pathname = usePathname()
  const router = useRouter()

  const isAuthenticated = customerInfo.customer !== null

  const handleFirstStepGoToNextStep = useCallback(() => {
    if (!isRerent && !isProlonging && !isEditMode && !touched.includeInsurance && !touched.includeDeductibles) {
      setValues({ ...values, includeInsurance: true, includeDeductibles: true })
      setTouched({ ...touched, includeInsurance: true, includeDeductibles: true })
    }

    goToNextStep()
  }, [goToNextStep, touched, isProlonging, isEditMode, values, setValues, setTouched])

  const isButtonDisabled = useMemo(
    () =>
      !values.pickupAt ||
      !values.returnAt ||
      (isProlonging && !touched.returnAt) ||
      (!values.selectedLocationId && !values.selectedRegionId),
    [
      values.pickupAt,
      values.returnAt,
      isProlonging,
      touched.returnAt,
      values.selectedLocationId,
      values.selectedRegionId,
    ],
  )

  const isRerentOrder = useMemo(() => isRerent, [isRerent])

  const getLoginRedirectUrl = () => {
    const rentedFrom = buildDate(values.startDate, values.pickupAt)
    const rentedFromStr = `rentedFrom=${rentedFrom.toISOString() ?? ''}`
    const rentedTo = buildDate(values.endDate, values.returnAt)
    const rentedToStr = `rentedTo=${rentedTo.toISOString() ?? ''}`
    const isDeductiblesStr = `isDeductibles=${values.includeDeductibles ?? 'false'}`
    const isInsuranceStr = `isInsurance=${values.includeInsurance ?? 'false'}`
    const locationIdStr = `locationId=${values.selectedLocationId ?? ''}`
    const regionIdStr = `regionId=${values.selectedRegionId ?? ''}`
    const isRerentStr = `isRerent=${isRerentOrder ?? 'false'}`
    const stepStr = `step=${step}`

    return `/login?redirectTo=${encodeURIComponent(
      `${pathname}?${rentedFromStr}&${rentedToStr}&${isDeductiblesStr}&${isInsuranceStr}&${locationIdStr}&${regionIdStr}&${isRerentStr}&${stepStr}`,
    )}`
  }

  const getNextButtonText = () => {
    if (nextButtonDisabled) {
      return t('next')
    }

    if (isRerent) {
      return t('continueWithRequest')
    }

    return t('continueWithRent')
  }

  const getButtonsOnProlongationSuccess = () => {
    return (
      <>
        <Button href="/cart">{t(isEditMode ? 'updatedGoToYourCart' : 'addedGoToYourCart')}</Button>
        <ButtonWhite href="/browse">{t('continueBrowsing')}</ButtonWhite>
      </>
    )
  }

  const getButtonsForFirstStep = () => {
    if (isRentalBeyondMaxTime) {
      return <Text textAlign="center">{t('maxRentalTimeValidationError')}</Text>
    }

    if (isProlonging) {
      if (isRerentOrder) {
        return (
          <QuotationRequestForm
            cartFormValues={values}
            product={product}
            productPrice={productPrice}
            disabled={isButtonDisabled}
            isProlonging={isProlonging}
            customerInfo={customerInfo}
            orderItem={orderItemtInfo}
          />
        )
      }

      return (
        <Button onClick={handleSubmit} isDisabled={isButtonDisabled}>
          {t('changeRentalPeriod')}
        </Button>
      )
    }

    if (isRerent && product.notRequestable) {
      return (
        <Text textAlign="center">
          {regionHasAvailableLocations === undefined ? t('notAvailableAtAll') : t('notAvailableOnDates')}
        </Text>
      )
    }

    return (
      <Button onClick={handleFirstStepGoToNextStep} isDisabled={nextButtonDisabled}>
        {getNextButtonText()}
      </Button>
    )
  }

  const getButtonsForSecondStep = () => {
    if (isRerentOrder) {
      if (!isAuthenticated) {
        {
          router.push(getLoginRedirectUrl())
        }
        return <></>
      }
      return (
        <QuotationRequestForm
          cartFormValues={values}
          goToPrevStep={goToPrevStep}
          product={product}
          productPrice={productPrice}
          isProlonging={isProlonging}
          customerInfo={customerInfo}
          orderItem={orderItemtInfo}
        />
      )
    }

    if (values.hasServices) {
      return (
        <SendServiceRequestForm
          cartFormValues={values}
          goToPrevStep={goToPrevStep}
          product={product}
          customerInfo={customerInfo}
        />
      )
    }

    return (
      <>
        <Box maxWidth={{ lg: '200px' }}>
          <ButtonSecondary onClick={goToPrevStep}>{t('editRentalPeriod')}</ButtonSecondary>
        </Box>
        <Box>
          <Button onClick={handleSubmit}>{t(isEditMode ? 'updateCart' : 'addToCart')}</Button>
        </Box>
      </>
    )
  }

  const content: JSX.Element | null = useMemo(() => {
    if (isSubmitting) {
      return <Loader />
    }

    if (submitStatus === 'success' && !isProlonging) {
      return getButtonsOnProlongationSuccess()
    }

    if (submitStatus === 'success') {
      // On regular success - no buttons
      return null
    }

    if (step === 1) {
      return getButtonsForFirstStep()
    }

    if (step === 2) {
      return getButtonsForSecondStep()
    }

    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    isProlonging,
    submitStatus,
    isSubmitting,
    isRentalBeyondMaxTime,
    goToPrevStep,
    handleFirstStepGoToNextStep,
    values.pickupAt,
    values.returnAt,
    handleSubmit,
    step,
    t,
    touched.returnAt,
    productPrice,
    nextButtonDisabled,
    isRerent,
  ])

  return (
    <>
      <Box marginBottom="20px">
        <OrderPreSummaryBox
          product={product}
          productPrice={productPrice}
          productLocations={productLocations}
          values={values}
          touched={touched}
          isRerent={isRerent}
          isProlonging={isProlonging}
          productPriceLoading={productPriceLoading}
        />
      </Box>
      {isRerentOrder ? <>{content}</> : <RentalSectionFooterWrapper>{content}</RentalSectionFooterWrapper>}
    </>
  )
}

export default RentalSectionFooter
